import React, { useState } from "react";
import { api } from "../lib/api";
import { FormModal, Field, TextArea } from "../pages/LeadsPage";
import { toast } from "sonner";
import { useTenant } from "../context/TenantContext";
import { useBusinessProfile } from "../hooks/useBusinessProfile";
import { Send, Link2, Copy, Check, Mail, Eye, Scissors, MessageSquare } from "lucide-react";

/** Modal for "Send Quote" — sends via email if configured, always offers a shareable link. */
export default function SendQuoteModal({ quote, onClose, onSplit }) {
  // Delivery channel toggle. Only `email` is live right now; `text` is
  // locked and tagged "coming soon" until we wire up Twilio. Keeping it
  // on-screen (greyed out) sets the expectation with the shop that it's
  // a planned feature so they stop asking.
  const [channel, setChannel] = useState("email");
  // Prefill from the customer on record so the admin doesn't have to
  // retype an email they already have on the job card. Legacy quotes
  // may only carry `email`; new records use `contact_email`.
  const [email, setEmail] = useState(
    (quote.contact_email || quote.email || "").trim()
  );
  const [message, setMessage] = useState("");
  const { tenant } = useTenant() || {};
  const { profile } = useBusinessProfile();
  const defaultSubject = `Quote - ${tenant?.name || profile?.name || "WrapConsole"}`;
  const [subject, setSubject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [emailResult, setEmailResult] = useState(null);
  const installCount = (quote.job_items || []).reduce((n, it) => n + (it.installations || []).length, 0);
  const [choice, setChoice] = useState(quote.customer_choice || "");
  const saveChoice = async (next) => {
    setChoice(next);
    try { await api.post(`/quotes/${quote.id}/customer-choice`, { customer_choice: next }); }
    catch (err) { toast.error(err.response?.data?.detail || "Couldn't save option setting"); }
  };

  React.useEffect(() => {
    // Pre-generate share link
    (async () => {
      try {
        const { data } = await api.post(`/quotes/${quote.id}/share`);
        setShareUrl(data.share_url);
      } catch {/* ignore */}
    })();
  }, [quote.id]);

  const send = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setEmailResult(null);
    try {
      const { data } = await api.post(`/quotes/${quote.id}/send`, { recipient_email: email, customer_choice: choice, subject: (subject ?? defaultSubject).trim() || defaultSubject, message });
      setShareUrl(data.share_url);
      setEmailResult(data);
      if (data.email?.sent) toast.success("Quote emailed");
      else if (!data.email_configured) toast.info("Email not configured — share link generated instead");
      else toast.error(data.email?.reason || "Email failed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    if (!shareUrl) return;
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success("Link copied");
    setTimeout(() => setCopied(false), 2000);
  };

  // Open the public-facing quote view in a new tab so the office can
  // sanity-check how the customer will see it before clicking Save.
  const preview = () => {
    if (!shareUrl) {
      toast.info("Still generating preview link — try again in a moment");
      return;
    }
    window.open(shareUrl, "_blank", "noopener,noreferrer");
  };

  // Split this multi-vehicle quote into N standalone quotes (one per
  // job_item) so the customer can see each as its own priced option
  // with its own share link / PDF / email, instead of a single
  // combined total. Archives the source and triggers a parent reload.
  const [splitting, setSplitting] = useState(false);
  const vehicleCount = Array.isArray(quote.job_items) ? quote.job_items.length : 0;
  const canSplit = vehicleCount > 1;
  const split = async () => {
    if (!canSplit) return;
    if (!window.confirm(
      `Split this quote into ${vehicleCount} separate quotes — one per vehicle? ` +
      `The customer will receive ${vehicleCount} individual quote links. ` +
      `This cannot be automatically undone.`
    )) return;
    setSplitting(true);
    try {
      const { data } = await api.post(`/quotes/${quote.id}/split`);
      toast.success(`Split into ${data.count} quotes`);
      onSplit?.(data);
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Split failed");
    } finally {
      setSplitting(false);
    }
  };

  return (
    <FormModal title={`Send quote — $${Number(quote.amount).toLocaleString()}`} onClose={onClose} onSubmit={send}>
      <div className="text-sm text-slate-400 -mt-1 mb-2">
        Send to {quote.contact_name} for their {quote.vehicle} — {quote.service}.
      </div>

      {/* Delivery channel selector — Email is live, Text is reserved
          for the planned Twilio integration. The Text option is left
          visible but disabled so shop owners can see it's coming.
          Clicking it does nothing; a tooltip explains why. */}
      <div>
        <div className="text-label mb-1.5">How to send</div>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setChannel("email")}
            data-testid="send-method-email"
            aria-pressed={channel === "email"}
            className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm transition ${
              channel === "email"
                ? "border-emerald-400/60 bg-emerald-500/15 text-white"
                : "border-white/10 bg-white/[0.03] text-slate-300 hover:bg-white/[0.06]"
            }`}
          >
            <span
              className={`w-4 h-4 rounded-md border flex items-center justify-center shrink-0 ${
                channel === "email" ? "bg-emerald-400 border-emerald-300" : "border-white/30"
              }`}
            >
              {channel === "email" && <Check className="w-3 h-3 text-black" />}
            </span>
            <Mail className="w-3.5 h-3.5" />
            <span>Email</span>
          </button>
          <button
            type="button"
            disabled
            data-testid="send-method-text"
            title="SMS delivery coming soon — needs Twilio to be wired up"
            className="flex items-center gap-2 rounded-xl border border-white/5 bg-white/[0.02] px-3 py-2.5 text-sm text-slate-500 cursor-not-allowed opacity-60 relative"
          >
            <span className="w-4 h-4 rounded-md border border-white/15 shrink-0" />
            <MessageSquare className="w-3.5 h-3.5" />
            <span>Text</span>
            <span className="ml-auto text-[9px] uppercase tracking-widest bg-white/[0.06] border border-white/10 rounded-full px-1.5 py-0.5">
              Soon
            </span>
          </button>
        </div>
      </div>

      {installCount > 1 && (
        <CustomerChoicePicker choice={choice} onChange={saveChoice} />
      )}

      <Field label="Recipient email" type="email" value={email} onChange={setEmail} testId="send-quote-email" required />
      <div>
        <Field label="Subject line" value={subject ?? defaultSubject} onChange={setSubject} testId="send-quote-subject" />
        {subject !== null && subject !== defaultSubject && (
          <button type="button" onClick={() => setSubject(null)} data-testid="send-quote-subject-reset"
            className="mt-1 text-[11px] text-sky-600 dark:text-sky-300 hover:underline">
            Reset to default ({defaultSubject})
          </button>
        )}
      </div>
      <TextArea label="Optional note to customer" value={message} onChange={setMessage} testId="send-quote-message" />

      <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-3 space-y-2">
        <div className="text-label">Shareable link</div>
        {shareUrl ? (
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-slate-300 truncate" data-testid="quote-share-url">
              {shareUrl}
            </code>
            <button type="button" onClick={copy} data-testid="quote-share-copy" className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs flex items-center gap-1">
              {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            </button>
          </div>
        ) : (
          <div className="text-xs text-slate-500">Generating link…</div>
        )}
        <div className="flex gap-2 text-[10px] text-slate-500">
          <Link2 className="w-3 h-3" /> Anyone with this link can view the quote (no login).
        </div>
      </div>

      {emailResult && (
        <div className={`text-xs rounded-xl px-3 py-2 ${emailResult.email?.sent ? "bg-emerald-50 text-emerald-800 border border-emerald-300 dark:bg-emerald-500/10 dark:text-emerald-200 dark:border-emerald-400/20" : "bg-rose-50 text-rose-800 border border-rose-300 dark:bg-rose-500/10 dark:text-rose-200 dark:border-rose-400/30"}`}>
          {emailResult.email?.sent
            ? `Email sent (id: ${emailResult.email.id})`
            : emailResult.email_configured
              ? `Email failed: ${emailResult.email.reason}`
              : "Email not configured. Share the link manually using the copy button above."}
        </div>
      )}

      {/* Preview + Split row — Preview opens the public quote in a new
          tab so the office can sanity-check it. Split is only shown when
          the quote has 2+ job_items; it splits the quote into N standalone
          quotes so each vehicle gets its own share link / PDF / email. */}
      <div className={canSplit ? "grid grid-cols-1 md:grid-cols-2 gap-2" : ""}>
        <button
          type="button"
          onClick={preview}
          disabled={!shareUrl}
          data-testid="quote-preview-btn"
          className="w-full flex items-center justify-center gap-2 text-sm bg-white/5 border border-white/10 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-full px-4 py-2.5 transition"
          title="Open the public quote in a new tab so you can see what the customer sees"
        >
          <Eye className="w-4 h-4" /> Preview as customer
        </button>
        {canSplit && (
          <button
            type="button"
            onClick={split}
            disabled={splitting}
            data-testid="quote-split-btn"
            className="w-full flex items-center justify-center gap-2 text-sm bg-sky-500/15 border border-sky-400/40 hover:bg-sky-500/25 disabled:opacity-50 text-sky-100 rounded-full px-4 py-2.5 transition"
            title={`Create ${vehicleCount} separate quotes — one per vehicle — so each gets its own share link, PDF, and email`}
          >
            <Scissors className="w-4 h-4" />
            {splitting
              ? "Splitting…"
              : `Split into ${vehicleCount} separate quotes`}
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={async () => {
          const a = document.createElement("a");
          const token = localStorage.getItem("wc_token");
          const res = await api.get(`/quotes/${quote.id}/pdf`, { responseType: "blob" });
          const url = URL.createObjectURL(res.data);
          a.href = url;
          a.download = `quote-${quote.id.slice(0, 8)}.pdf`;
          a.click();
          URL.revokeObjectURL(url);
        }}
        className="w-full flex items-center justify-center gap-2 text-sm bg-white/5 border border-white/10 hover:bg-white/10 text-white rounded-full px-4 py-2.5"
        data-testid="quote-download-pdf"
      >
        <Mail className="w-4 h-4" /> Download PDF instead
      </button>
    </FormModal>
  );
}


const CHOICE_MODES = [
  { id: "any", label: "Tick any they want", hint: "Customer can accept one, some or all" },
  { id: "one", label: "Pick exactly one", hint: "For comparing alternatives, e.g. partial vs full" },
];

function CustomerChoicePicker({ choice, onChange }) {
  const on = choice === "any" || choice === "one";
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3 space-y-2" data-testid="send-quote-choice">
      <label className="flex items-start gap-2.5 cursor-pointer">
        <input
          type="checkbox" checked={on} data-testid="send-quote-choice-toggle"
          onChange={(e) => onChange(e.target.checked ? "any" : "")}
          className="mt-0.5 w-4 h-4 accent-sky-500"
        />
        <span>
          <span className="block text-sm text-white font-medium">Price each installation as a separate option</span>
          <span className="block text-[11px] text-slate-400">The customer sees each installation's own price and chooses — no combined total.</span>
        </span>
      </label>
      {on && (
        <div className="grid grid-cols-2 gap-2 pl-6">
          {CHOICE_MODES.map((m) => (
            <button
              key={m.id} type="button" onClick={() => onChange(m.id)} aria-pressed={choice === m.id}
              data-testid={`send-quote-choice-${m.id}`}
              className={`text-left rounded-xl border px-3 py-2 transition-colors ${choice === m.id
                ? "border-sky-400/60 bg-sky-500/15 text-white"
                : "border-white/10 bg-white/[0.02] text-slate-300 hover:bg-white/[0.06]"}`}
            >
              <div className="text-xs font-semibold">{m.label}</div>
              <div className="text-[10px] text-slate-400 mt-0.5">{m.hint}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
